import {
  parseSearchArrayValue,
  parseSearchBooleanValue,
  parseSearchNumberValue,
  parseSearchStringValue,
} from 'infrastructure/functions/parse-query-string';
import { useQueryFilterNew } from 'infrastructure/hooks/common/use-query-param-new';
import { Assignee, OverviewDashboardSortEnum } from 'infrastructure/enums';
import { endOfDay, startOfMonth } from 'date-fns';
import useUser from 'utils/useUser';

import type { ParentClinicOption } from 'infrastructure/enums/parent-clinic';
import type {
  CroStatuses,
  InteractionStatusEnum,
  Languages,
  ReadingStatus,
  ReadingType,
  Timezones,
} from 'infrastructure/enums';
import type { TParseFunctions } from 'infrastructure/hooks/common/use-query-param-new';

export type TOverviewDashboardFilter = {
  page: number;
  reviewed: boolean;
  sorting: OverviewDashboardSortEnum;
  assignee?: Assignee;
  clinicGuid?: string;
  readingType?: ReadingType;
  status?: ReadingStatus;
  clinicState?: string;
  interactiveTimeRange?: string;
  interactionStatus?: InteractionStatusEnum;
  language?: Languages;
  timezone?: Timezones;
  parentClinicGuid?: string;
  patientGuid?: string;
  actionedByAgencyDoctorGuid?: string;
  startTime: number;
  endTime: number;
  parentClinicOption?: ParentClinicOption;
  croStatus?: CroStatuses[];
  doctorGuid?: string;
  showActionedByAgency?: boolean;
};

const baseDefaultFilter: TOverviewDashboardFilter = {
  page: 1,
  assignee: Assignee.Free,
  reviewed: false,
  startTime: startOfMonth(new Date()).getTime(),
  endTime: endOfDay(new Date()).getTime(),
  sorting: OverviewDashboardSortEnum.Newest,
};

const parseFunctions: TParseFunctions<TOverviewDashboardFilter> = {
  page: parseSearchNumberValue,
  clinicGuid: parseSearchStringValue,
  readingType: parseSearchStringValue,
  assignee: parseSearchStringValue,
  status: parseSearchStringValue,
  sorting: parseSearchStringValue,
  reviewed: parseSearchBooleanValue,
  clinicState: parseSearchStringValue,
  interactionStatus: parseSearchStringValue,
  startTime: parseSearchNumberValue,
  endTime: parseSearchNumberValue,
  interactiveTimeRange: parseSearchStringValue,
  language: parseSearchStringValue,
  timezone: parseSearchStringValue,
  parentClinicGuid: parseSearchStringValue,
  patientGuid: parseSearchStringValue,
  parentClinicOption: parseSearchStringValue,
  actionedByAgencyDoctorGuid: parseSearchStringValue,
  croStatus: parseSearchArrayValue,
  doctorGuid: parseSearchStringValue,
  showActionedByAgency: parseSearchBooleanValue,
};

export const useOverviewDashboardFilter = () => {
  const { isPractice, hasClinicalRole } = useUser();

  const defaultFilter: TOverviewDashboardFilter = {
    ...baseDefaultFilter,
    ...(isPractice && hasClinicalRole && { showActionedByAgency: false }),
  };

  return useQueryFilterNew<TOverviewDashboardFilter>(
    defaultFilter,
    parseFunctions,
    ['page', 'endTime'],
  );
};
